/**
 * Tiny status banner. Visible only when something is wrong (reconnecting or
 * closed); silent on idle/open so it doesn't add chrome to a working game.
 * Owner: shared component. Phase-3 A3.1/A3.3 may restyle the visuals but the
 * banner's *presence* is part of the resilience UX (PRD G7).
 *
 * Phase 11 (v1.2): wrap the rendered banner in `<AnimatePresence>` + `<m.div>`
 * so it slides in from the top edge when network state goes off-idle, and
 * slides back out when the connection returns to `open`. `layout="position"`
 * is intentionally NOT set — the banner is fixed-positioned and the slide
 * animation is the only motion we want on it. `MotionConfig reducedMotion`
 * in App.tsx snaps the slide off when the OS asks for reduced motion.
 */
import { AnimatePresence, m } from "motion/react";
import { useConnection } from "../state/hooks.js";

const ENTER = { y: -28, opacity: 0 };
const SHOW = { y: 0, opacity: 1 };
const EXIT = { y: -28, opacity: 0 };
const BANNER_TRANSITION = {
  type: "spring" as const,
  stiffness: 360,
  damping: 28,
  mass: 0.5,
};

export function ConnectionBanner(): JSX.Element {
  const c = useConnection();
  const isVisible = c.kind !== "idle" && c.kind !== "open";
  const text =
    c.kind === "connecting"
      ? "Connecting…"
      : c.kind === "reconnecting"
        ? `Reconnecting (attempt ${c.attempt})…`
        : c.kind === "closed"
          ? `Disconnected: ${c.reason}`
          : "";
  return (
    <AnimatePresence>
      {isVisible ? (
        <m.div
          key={c.kind}
          role="status"
          aria-live="polite"
          className={`conn-banner conn-banner-${c.kind}`}
          initial={ENTER}
          animate={SHOW}
          exit={EXIT}
          transition={BANNER_TRANSITION}
        >
          <span className="conn-banner-dot" aria-hidden="true" />
          <span className="conn-banner-text">{text}</span>
        </m.div>
      ) : null}
    </AnimatePresence>
  );
}
